import React, { useEffect, useRef, useState } from 'react';
import { PostcodeFormProps } from '../types';
import trackEvent from '../../../../../helpers/gtm/eventPush';

const PostcodeLookupForm: React.FC<PostcodeFormProps> = (props) => {
  const {
    handleSearch,
    handlePostcodeChange,
    resetParam,
    serviceError,
    storedAddress,
    trackingContext,
    validatePostcode,
  } = props;

  // State management
  const [inputValue, setInputValue] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Checking if reset has been triggered
  useEffect(() => {
    if (resetParam) {
      setInputValue('');
      const { current } = inputRef;
      if (current) {
        current.value = '';
        current.focus();
      }
    }
  }, [resetParam]);

  // Checking for a stored postcode in the session
  useEffect(() => {
    const { postcode: value } = storedAddress;
    const valid = validatePostcode(value);
    if (!hasLoaded && value) {
      const { current } = inputRef;
      if (current) {
        current.value = value;
      }
      setHasLoaded(true);
      setIsValid(valid);
    }
  }, [
    handlePostcodeChange,
    inputValue,
    hasLoaded,
    storedAddress,
    validatePostcode,
  ]);

  // Function to handle input change
  const handleInputChange = (event): void => {
    const { value } = event.target;
    const valid = validatePostcode(value);
    setIsValid(valid);
    handlePostcodeChange(value, valid);
    setValidationError('');
  };

  // Function to handle input keydown
  const handleInputKeyDown = (event): boolean | void => {
    // Prevent enter key submitting form
    if (event.keyCode === 13) {
      event.preventDefault();
      handleButtonClick();
      return false;
    }
  };

  // Function to handle input invalid
  const handleInputInvalid = (event): void => {
    const { value } = event.target;
    if (!value) {
      trackEvent({
        category: 'Form error',
        action: `${trackingContext}_st_1:select#postcodeLookup`,
        label: 'Please enter a valid postcode|field=',
      });
    }
  };

  // Function to handle button click
  const handleButtonClick = (): void => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const { value } = input;
    const valid = validatePostcode(input.value);
    setIsValid(valid);

    // When query is not valid
    if (!valid) {
      setValidationError('Please enter a valid postcode');

      // Send tracking data
      trackEvent({
        category: 'Form error',
        action: `${trackingContext}_st_1:select#postcodeLookup`,
        label: `Please enter a valid postcode|field=${value}`,
      });
    }

    // When no query or query is not valid
    // focus the input at the end of the range.
    if (!value || !valid) {
      input.setSelectionRange(input.value.length, input.value.length);
      input.selectionStart = input.value.length;
      input.focus();
    }

    // Do the search
    handleSearch(value, valid);
  };

  return (
    <>
      <div className="form-group form-group--address-lookup">
        <input
          ref={inputRef}
          onInput={handleInputChange}
          onInvalid={handleInputInvalid}
          onKeyDown={handleInputKeyDown}
          className={`form-group__input form-group__input--postcode ${
            validationError ? 'form-group__input--postcode-error' : ''
          }`}
          type="text"
          name="address-postcode"
          autoComplete="postal-code"
          pattern="[a-zA-Z0-9\s]+"
          maxLength={8}
          required={true}
          aria-invalid={isValid ? 'false' : 'true'}
          aria-label="Enter your postcode to find your address"
        />
        <button
          className="button button--secondary button--postcode"
          type="button"
          onClick={handleButtonClick}
        >
          Find address
        </button>
      </div>
      {serviceError ? (
        <div className="address_postcode__service-error">{serviceError}</div>
      ) : validationError ? (
        <div className="address_postcode__service-error" aria-live="assertive">
          {validationError}
        </div>
      ) : null}
    </>
  );
};

export default PostcodeLookupForm;
